import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { MisPermisosResponse, PermisosBulkPayload, PermisosUsuarioResponse } from '../models/permiso.model';

/**
 * Sistema de Permisos granulares por usuario (task #233). Todo el router
 * backend está detrás de `require_superadmin` — cualquier método de este
 * servicio puede devolver 403 si el admin logueado no tiene
 * `is_superadmin=1`.
 *
 * `getPermisos()` ya trae `nombre`/`cargo` del usuario junto con el catálogo
 * (ver `PermisosUsuarioResponse`), así que la página consumidora no necesita
 * pegarle aparte a `UsuariosAdminService.get()` solo para el header.
 */
@Injectable({ providedIn: 'root' })
export class PermisosService {
  private readonly base = `${environment.apiUrl}/api/v1/admin`;
  private readonly authBase = `${environment.apiUrl}/api/v1/auth`;

  /** Cache in-memory de los permisos del user actual (feature 2026-09-17).
   * shareReplay(1) para no golpear la BD cada vez que un wizard chequea el
   * gate. Se invalida al login/logout via `invalidarMisPermisos()`. */
  private misPermisos$: Observable<MisPermisosResponse> | null = null;
  private ultimoDocCacheado: string | null = null;

  constructor(private http: HttpClient, private auth: AuthService) {
    // Invalidar cache cuando el user cambia (login/logout) — sino la próxima
    // consulta devolvería los permisos del usuario anterior.
    this.auth.currentUser$.subscribe((u) => {
      const doc = u?.documento ?? null;
      if (doc !== this.ultimoDocCacheado) {
        this.misPermisos$ = null;
        this.ultimoDocCacheado = doc;
      }
    });
  }

  getPermisos(doc: string): Observable<PermisosUsuarioResponse> {
    return this.http.get<PermisosUsuarioResponse>(`${this.base}/usuarios/${doc}/permisos`);
  }

  /** Reemplazo bulk transaccional. `ids: []` es válido y explícito (modo legacy). */
  savePermisos(doc: string, ids: number[]): Observable<PermisosUsuarioResponse> {
    const body: PermisosBulkPayload = { permisos_ids: ids };
    return this.http.put<PermisosUsuarioResponse>(`${this.base}/usuarios/${doc}/permisos`, body);
  }

  /** Permisos granulares del user logueado, para gates de UI. Cacheado
   * in-memory con shareReplay(1). Ver `MisPermisosResponse` para la lógica. */
  getMisPermisos(): Observable<MisPermisosResponse> {
    if (!this.misPermisos$) {
      this.misPermisos$ = this.http
        .get<MisPermisosResponse>(`${this.authBase}/mis-permisos`)
        .pipe(shareReplay(1));
    }
    return this.misPermisos$;
  }

  /** Chequeo puntual: ¿el user actual tiene el permiso `id`? Superadmin
   * bypasa. Modo legacy (`sin_permisos_asignados`) = no lo tiene, salvo que
   * sea superadmin. */
  tienePermiso(id: number): Observable<boolean> {
    return this.getMisPermisos().pipe(
      map((r) => r.is_superadmin || r.permisos_ids.includes(id)),
    );
  }

  /** Llamar al login/logout para no reusar el cache entre usuarios. */
  invalidarMisPermisos(): void {
    this.misPermisos$ = null;
  }
}
